import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Building2,
  UserPlus,
  CheckCircle2,
  LogOut,
} from "@/components/icons";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { FlatInput, PrimaryButton } from "./Login";

/**
 * Onboarding — estilo minimalista "Files Management".
 * Paleta blanco / gris `#F2F3F5` / negro. Sin verde.
 *
 * Mandatory onboarding for newly-registered doctors:
 *  Step 1 — create first office (name, address, phone)
 *  Step 2 — create their secretary (name, email, password, phone)
 */
export default function Onboarding() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1 = office, 2 = secretary
  const [createdOffice, setCreatedOffice] = useState(null);
  const [office, setOffice] = useState({ name: "", address: "", phone: "", logo_url: "" });
  const [sec, setSec] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // If the doctor lands here but already has an office, skip ahead.
  useEffect(() => {
    if (user?.role !== "doctor") return;
    api.get("/offices").then((r) => {
      if (r.data && r.data.length > 0) {
        const first = r.data[0];
        setCreatedOffice(first);
        if (first.secretary_id) {
          nav("/doctor", { replace: true });
        } else {
          setStep(2);
        }
      }
    });
  }, [user, nav]);

  const submitOffice = async (e) => {
    e.preventDefault();
    setErr("");
    if (!office.name.trim()) return setErr("Ingresa el nombre de la oficina");
    setLoading(true);
    try {
      const { data } = await api.post("/offices", {
        name: office.name.trim(),
        address: office.address.trim() || null,
        phone: office.phone.trim() || null,
        logo_url: office.logo_url || null,
      });
      setCreatedOffice(data);
      toast.success("Oficina creada");
      setStep(2);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const submitSecretary = async (e) => {
    e.preventDefault();
    setErr("");
    if (!sec.name.trim()) return setErr("Ingresa el nombre de la secretaria");
    if (!sec.email.trim()) return setErr("Ingresa el email de la secretaria");
    if (sec.password.length < 6)
      return setErr("La contraseña debe tener al menos 6 caracteres");
    setLoading(true);
    try {
      await api.delete(`/offices/${createdOffice.id}`);
      const { data } = await api.post("/offices", {
        name: createdOffice.name,
        address: createdOffice.address,
        phone: createdOffice.phone,
        logo_url: createdOffice.logo_url || null,
        secretary_name: sec.name.trim(),
        secretary_email: sec.email.trim().toLowerCase(),
        secretary_password: sec.password,
      });
      setCreatedOffice(data);
      toast.success("Secretaria creada — ¡Todo listo!");
      nav("/doctor", { replace: true });
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-white p-4 sm:p-8"
      data-testid="onboarding-page"
    >
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <img
              src="/brand/consudoctor-icon.png"
              alt="ConsuDoctor"
              className="w-10 h-10 object-contain"
            />
            <div>
              <div className="text-base font-semibold text-black leading-tight">
                ConsuDoctor
              </div>
              <div className="text-xs text-black/50">
                Configura tu consultorio en 2 pasos
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              logout();
              nav("/login", { replace: true });
            }}
            data-testid="onboarding-logout"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-black/60 hover:text-black px-3 py-2 rounded-lg hover:bg-[#F2F3F5] transition"
          >
            <LogOut className="w-3.5 h-3.5" /> Salir
          </button>
        </div>

        {/* Stepper */}
        <Stepper step={step} />

        {/* Step 1: Office */}
        {step === 1 && (
          <form
            onSubmit={submitOffice}
            className="mt-8 space-y-3"
            data-testid="onboarding-office-form"
          >
            <StepHeader
              icon={Building2}
              title="Crea tu oficina"
              subtitle="Es el espacio físico donde atiendes pacientes."
            />

            <LogoUploader
              value={office.logo_url}
              onChange={(url) => setOffice({ ...office, logo_url: url })}
            />

            <FlatInput
              label="Nombre de la oficina"
              value={office.name}
              onChange={(v) => setOffice({ ...office, name: v })}
              placeholder="Consultorio Centro"
              testId="ob-office-name"
            />
            <AddressField
              value={office.address}
              onChange={(v) => setOffice({ ...office, address: v })}
            />
            <FlatInput
              label="Teléfono"
              type="tel"
              value={office.phone}
              onChange={(v) => setOffice({ ...office, phone: v })}
              placeholder="+595 999 111 222"
              testId="ob-office-phone"
              required={false}
            />

            {err && (
              <div
                data-testid="ob-error"
                className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5"
              >
                {err}
              </div>
            )}

            <PrimaryButton
              type="submit"
              loading={loading}
              testId="ob-office-submit"
            >
              Siguiente
            </PrimaryButton>
          </form>
        )}

        {/* Step 2: Secretary */}
        {step === 2 && (
          <form
            onSubmit={submitSecretary}
            className="mt-8 space-y-3"
            data-testid="onboarding-secretary-form"
          >
            <StepHeader
              icon={UserPlus}
              title="Invita a tu secretaria"
              subtitle="Le crearás un acceso para gestionar tu agenda y cobros con el email y contraseña que definas aquí."
            />

            <FlatInput
              label="Nombre completo"
              value={sec.name}
              onChange={(v) => setSec({ ...sec, name: v })}
              placeholder="Lic. María González"
              testId="ob-sec-name"
              autoComplete="name"
            />
            <FlatInput
              label="Email"
              type="email"
              value={sec.email}
              onChange={(v) => setSec({ ...sec, email: v })}
              placeholder="secretaria@consudoctor.com"
              testId="ob-sec-email"
              autoComplete="email"
            />
            <FlatInput
              label="Contraseña"
              type="password"
              value={sec.password}
              onChange={(v) => setSec({ ...sec, password: v })}
              placeholder="Mín. 6 caracteres"
              testId="ob-sec-password"
              autoComplete="new-password"
            />
            <FlatInput
              label="Teléfono (opcional)"
              type="tel"
              value={sec.phone}
              onChange={(v) => setSec({ ...sec, phone: v })}
              placeholder="+595 999 111 222"
              testId="ob-sec-phone"
              autoComplete="tel"
              required={false}
            />

            {err && (
              <div
                data-testid="ob-error"
                className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5"
              >
                {err}
              </div>
            )}

            <PrimaryButton
              type="submit"
              loading={loading}
              testId="ob-sec-submit"
            >
              Finalizar
            </PrimaryButton>

            <button
              type="button"
              onClick={() => {
                setErr("");
                setStep(1);
              }}
              className="w-full text-center text-xs font-medium text-black/60 hover:text-black transition mt-3"
            >
              ← Volver al paso anterior
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Helpers
// =========================================================================
function Stepper({ step }) {
  const items = [
    { n: 1, label: "Oficina" },
    { n: 2, label: "Secretaria" },
  ];
  return (
    <div className="flex items-center justify-center gap-3">
      {items.map((it, idx) => {
        const done = step > it.n;
        const active = step === it.n;
        return (
          <div key={it.n} className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition ${
                  done
                    ? "bg-black text-white"
                    : active
                    ? "bg-black text-white"
                    : "bg-[#F2F3F5] text-black/40"
                }`}
              >
                {done ? <CheckCircle2 className="w-4 h-4" /> : it.n}
              </div>
              <div
                className={`text-sm font-medium ${
                  active || done ? "text-black" : "text-black/40"
                }`}
              >
                {it.label}
              </div>
            </div>
            {idx < items.length - 1 && (
              <div className="w-10 h-px bg-black/10" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function StepHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-[#F2F3F5] text-black flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="pt-0.5">
        <div className="text-lg font-semibold text-black tracking-tight leading-tight">
          {title}
        </div>
        <div className="text-xs text-black/55 mt-1 leading-relaxed">
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function AddressField({ value, onChange }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-black/50 font-semibold mb-1.5 px-1">
        Dirección
      </span>
      <AddressAutocomplete
        value={value}
        onChange={onChange}
        placeholder="Av. Libertador 1234, Of. 5B"
        testid="ob-office-address"
        showIcon={false}
        required={false}
        inputClassName="w-full bg-[#F2F3F5] hover:bg-[#EDEEF0] focus:bg-white border border-transparent focus:border-black/80 rounded-xl px-4 py-3.5 text-black text-[15px] placeholder-black/30 outline-none transition-all"
      />
    </label>
  );
}

function LogoUploader({ value, onChange }) {
  const inputRef = useRef(null);
  const [err, setErr] = useState("");

  const onPick = (file) => {
    setErr("");
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErr("Selecciona una imagen (PNG, JPG, etc.).");
      return;
    }
    if (file.size > 1024 * 1024) {
      setErr("La imagen debe pesar menos de 1 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => onChange(e.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <span className="block text-[11px] uppercase tracking-wider text-black/50 font-semibold mb-1.5 px-1">
        Logo de la oficina (opcional)
      </span>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid="ob-office-logo-trigger"
          className="w-16 h-16 rounded-xl bg-[#F2F3F5] hover:bg-[#EDEEF0] border border-dashed border-black/15 hover:border-black/40 flex items-center justify-center overflow-hidden transition flex-shrink-0"
        >
          {value ? (
            <img src={value} alt="Logo" className="w-full h-full object-cover" />
          ) : (
            <Building2 className="w-6 h-6 text-black/30" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="text-sm font-semibold text-black hover:underline underline-offset-4 transition"
          >
            {value ? "Cambiar logo" : "Subir logo"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="block text-xs text-red-600 hover:underline mt-1"
            >
              Quitar
            </button>
          )}
          <div className="text-[11px] text-black/45 mt-1">
            PNG o JPG · máx 1 MB
          </div>
          {err && <div className="text-xs text-red-700 mt-1">{err}</div>}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0])}
          data-testid="ob-office-logo-input"
        />
      </div>
    </div>
  );
}
